import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Plus, Edit, Trash2, Tag, Search as SearchIcon } from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  code: '',
  param_values: [],
  next_statuses: [],
  counts_in_conversions: true,
  counts_in_revenue: true,
  offer_capacity: true,
  label_color: '#6366f1',
};

function slugify(name) {
  return (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export default function ConversionTypes() {
  const { t } = useT();
  const [builtin, setBuiltin] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/conversion-types');
      setBuiltin(res.data.builtin || []);
      setTypes(res.data.custom || []);
    } catch (err) {
      console.error('Error loading conversion types:', err);
      toast.error(t('Error loading conversion types'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const addParam = () =>
    setForm((prev) => ({ ...prev, param_values: [...prev.param_values, ''] }));
  const updateParam = (idx, value) =>
    setForm((prev) => ({
      ...prev,
      param_values: prev.param_values.map((p, i) => (i === idx ? value : p)),
    }));
  const removeParam = (idx) =>
    setForm((prev) => ({
      ...prev,
      param_values: prev.param_values.filter((unused, i) => i !== idx),
    }));

  const statusOptions = () => {
    const codes = [...builtin];
    types.forEach((tp) => {
      if (tp.code && !codes.includes(tp.code) && (!editing || tp.id !== editing.id)) {
        codes.push(tp.code);
      }
    });
    return codes;
  };

  const toggleNextStatus = (code) =>
    setForm((prev) => ({
      ...prev,
      next_statuses: prev.next_statuses.includes(code)
        ? prev.next_statuses.filter((c) => c !== code)
        : [...prev.next_statuses, code],
    }));

  const payloadFromForm = () => ({
    name: form.name.trim(),
    code: (form.code || '').trim() || slugify(form.name),
    param_values: form.param_values.map((p) => p.trim()).filter(Boolean),
    next_statuses: form.next_statuses,
    counts_in_conversions: form.counts_in_conversions,
    counts_in_revenue: form.counts_in_revenue,
    offer_capacity: form.offer_capacity,
    label_color: form.label_color || null,
  });

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    try {
      const res = await axios.post(API + '/conversion-types', payloadFromForm());
      setTypes((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Conversion type created'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      console.error('Error creating conversion type:', err);
      toast.error(detail || t('Error creating conversion type'));
    }
  };

  const openEdit = (tp) => {
    setEditing(tp);
    setForm({
      name: tp.name || '',
      code: tp.code || '',
      param_values: tp.param_values || [],
      next_statuses: tp.next_statuses || [],
      counts_in_conversions: tp.counts_in_conversions !== false,
      counts_in_revenue: tp.counts_in_revenue !== false,
      offer_capacity: tp.offer_capacity !== false,
      label_color: tp.label_color || '#6366f1',
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    try {
      const res = await axios.put(API + '/conversion-types/' + editing.id, payloadFromForm());
      setTypes((prev) => prev.map((tp) => (tp.id === editing.id ? res.data : tp)));
      setEditOpen(false);
      setEditing(null);
      resetForm();
      toast.success(t('Conversion type updated'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      console.error('Error updating conversion type:', err);
      toast.error(detail || t('Error updating conversion type'));
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await axios.delete(API + '/conversion-types/' + id);
      setTypes((prev) => prev.filter((tp) => tp.id !== id));
      setDeleteTarget(null);
      toast.success(t('Conversion type deleted'));
    } catch (err) {
      console.error('Error deleting conversion type:', err);
      toast.error(t('Error deleting conversion type'));
    }
  };

  const filtered = types.filter((tp) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (tp.name || '').toLowerCase().includes(q) ||
      (tp.code || '').toLowerCase().includes(q)
    );
  });

  const renderForm = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('e.g. Second deposit')}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Code (slug)')}</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder={slugify(form.name) || 'second_deposit'}
          />
          <p className="text-xs text-muted-foreground">
            {t('Canonical status stored on each conversion. Auto-generated from the name if left blank.')}
          </p>
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>{t('Network status values')}</Label>
          <Button variant="outline" size="sm" onClick={addParam}>
            <Plus className="w-4 h-4 mr-1" />
            {t('Add value')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('Raw status strings partner networks send that should map to this type (case-insensitive).')}
        </p>
        {form.param_values.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('No status values added')}</p>
        ) : (
          <div className="space-y-2">
            {form.param_values.map((p, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_auto] gap-2">
                <Input
                  value={p}
                  onChange={(e) => updateParam(idx, e.target.value)}
                  placeholder={t('Network status (e.g. ftd)')}
                />
                <Button variant="ghost" size="sm" aria-label={t('Remove value')} title={t('Remove value')} onClick={() => removeParam(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-3">
        <Label>{t('Allowed next statuses')}</Label>
        <p className="text-xs text-muted-foreground">
          {t('Transition rules: which statuses a conversion in this type may move to. Leave all unchecked to allow any transition.')}
        </p>
        <div className="flex flex-wrap gap-2">
          {statusOptions().map((code) => {
            const active = form.next_statuses.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggleNextStatus(code)}
                className={
                  'px-3 py-1 rounded-full border text-sm transition-colors capitalize ' +
                  (active
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40')
                }
              >
                {code}
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>{t('Counts in conversions')}</Label>
            <p className="text-xs text-muted-foreground">{t('Include this type in the conversions column.')}</p>
          </div>
          <Switch
            checked={form.counts_in_conversions}
            onCheckedChange={(v) => setForm((prev) => ({ ...prev, counts_in_conversions: v }))}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>{t('Counts in revenue')}</Label>
            <p className="text-xs text-muted-foreground">{t('Include this type in the revenue column.')}</p>
          </div>
          <Switch
            checked={form.counts_in_revenue}
            onCheckedChange={(v) => setForm((prev) => ({ ...prev, counts_in_revenue: v }))}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>{t('Counts against offer cap')}</Label>
            <p className="text-xs text-muted-foreground">{t('Count conversions of this type toward the offer daily cap.')}</p>
          </div>
          <Switch
            checked={form.offer_capacity}
            onCheckedChange={(v) => setForm((prev) => ({ ...prev, offer_capacity: v }))}
          />
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label>{t('Label color')}</Label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={form.label_color || '#6366f1'}
            onChange={(e) => setForm((prev) => ({ ...prev, label_color: e.target.value }))}
            className="h-9 w-14 rounded border border-border bg-transparent cursor-pointer"
          />
          <Input
            value={form.label_color || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, label_color: e.target.value }))}
            placeholder="#6366f1"
            className="max-w-[160px]"
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Tools')}
        title={t('Conversion types')}
        subtitle={t('Define custom conversion types, map network statuses and control reporting semantics.')}
        actions={
          <Dialog
            open={createOpen}
            onOpenChange={(open) => {
              setCreateOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Create type')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('Create conversion type')}</DialogTitle>
                <DialogDescription>
                  {t('A custom conversion type with its own reporting rules and status transitions.')}
                </DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
          {t('Built-in statuses')}
        </Label>
        <div className="flex flex-wrap gap-2">
          {builtin.map((code) => (
            <Badge key={code} variant="secondary" className="capitalize">
              {code}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('Search conversion types')}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={Tag}
                  title={t('No custom conversion types yet')}
                  subtitle={t('Create one to map partner statuses like FTD or second deposit.')}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          filtered.map((tp) => (
            <Card key={tp.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: tp.label_color || 'rgb(var(--primary))' }}
                    />
                    <CardTitle className="truncate">{tp.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" aria-label={t('Edit conversion type')} title={t('Edit conversion type')} onClick={() => openEdit(tp)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={t('Delete conversion type')} title={t('Delete conversion type')} onClick={() => setDeleteTarget(tp)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Badge variant="outline" className="w-fit font-mono text-xs">
                  {tp.code}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {(tp.param_values || []).length > 0 ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Network status values')}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tp.param_values.map((v) => (
                        <Badge key={v} variant="secondary" className="text-xs">{v}</Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
                {(tp.next_statuses || []).length > 0 ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Allowed next statuses')}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tp.next_statuses.map((v) => (
                        <Badge key={v} variant="outline" className="text-xs capitalize">{v}</Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  {tp.counts_in_conversions !== false ? (
                    <Badge variant="secondary" className="text-xs">{t('In conversions')}</Badge>
                  ) : null}
                  {tp.counts_in_revenue !== false ? (
                    <Badge variant="secondary" className="text-xs">{t('In revenue')}</Badge>
                  ) : null}
                  {tp.offer_capacity !== false ? (
                    <Badge variant="secondary" className="text-xs">{t('Counts vs cap')}</Badge>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) {
            setEditing(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit conversion type')}</DialogTitle>
            <DialogDescription>
              {t('Update reporting semantics and status transitions.')}
            </DialogDescription>
          </DialogHeader>
          {renderForm()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleUpdate}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('Delete conversion type')}
        description={t('This permanently deletes the conversion type. Existing conversions keep their stored status.')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
